import { useToggle } from 'ahooks';
import { OrderRow } from '../OrderRow';
import { useTranslate } from '@tolgee/react';
import { useReturnStatusDisplay } from 'views/Conversation/Sider/hooks/useReturnStatusDisplay';
import { CopyText } from '../../CopyText';
import { useReturnReasonLabel } from 'views/Conversation/Sider/hooks/useReturnReasonLabel';
import { useReturnTimeTip } from 'views/Conversation/Sider/hooks/useReturnTimeTip';
import { ProductInfoImage } from '../../ProductInfo/Image';
import { Button, Popover } from 'antd';
import { OrderProduct } from '../OrderProduct';
import { getOrderProductPropsFromTradeProduct } from 'components/LightConversation/utils/order';
import operateBtns from 'components/Refund/OperateBtns';
import { toUpperCase } from 'utils/toLowerCase';
import { useOrderCard } from '../context';
import { useCallback } from 'react';
import { getPopupContainer } from 'components/LightConversation/utils/popover';

export interface ReturnRecord {
  unit: string;
  platform: Conversation.Trade.Vo['platform'];
  internalStoreId: string;
  externalStoreId: string;
  internalTradeId: string;
  externalTradeId: string;
  region: General.UpperCaseRegion;
  returnSn: string;
  displayStatus: Conversation.Trade.IDisplayStatus;
  items: Conversation.Trade.Product[];
  refundReason: Conversation.Trade.RefundReason;
  returnOrderItemId?: string;
}

interface StatusTextProps {
  platform: Conversation.Trade.Vo['platform'];
  displayStatus: Conversation.Trade.RefundReason['displayStatus'];
}

const StatusText = (props: StatusTextProps) => {
  const { platform, displayStatus } = props;
  const { displayStatusText } = useReturnStatusDisplay(platform, displayStatus);

  return <span className="text-[var(--fett-text-primary)]">{displayStatusText}</span>;
};

const ItemsBox = (props: {
  unit: string;
  platform: General.Platform;
  items: Conversation.Trade.Product[];
}) => {
  const { unit, platform, items } = props;

  const content = (
    <div className="flex flex-col gap-2 max-w-[420px]">
      {items.map((item) => {
        const dataProps = getOrderProductPropsFromTradeProduct(item);
        return (
          <OrderProduct simple key={item.itemId} {...dataProps} unit={unit} platform={platform} />
        );
      })}
    </div>
  );

  return (
    <Popover content={content} getPopupContainer={getPopupContainer}>
      <div className="inline-flex gap-3 max-w-fit">
        {items.map((item) => (
          <ProductInfoImage
            key={item.itemId}
            imgUrl={item.itemPic}
            itemName={item.itemName}
            platform={platform}
          />
        ))}
      </div>
    </Popover>
  );
};

const Operations = (props: ReturnRecord & { onUpdate: () => void }) => {
  const {
    platform,
    internalStoreId,
    externalStoreId,
    internalTradeId,
    externalTradeId,
    region,
    unit,
    returnSn,
    displayStatus,
    refundReason,
    onUpdate,
    returnOrderItemId,
  } = props;
  const { operate, refundAmount, latestOfferAmount, allowOffer, offerCounterLimit, preDispute } =
    refundReason;
  const { t } = useTranslate();
  const { openOrderDrawer } = useOrderCard();

  const optBtns = operateBtns({
    platform: toUpperCase(platform),
    operate,
    refreshList: onUpdate,
    internalStoreId,
    externalStoreId,
    internalTradeId,
    externalTradeId,
    returnOrderItemId: returnOrderItemId ? [returnOrderItemId] : undefined,
    returnSn,
    unit,
    refundAmount,
    latestOfferAmount,
    allowOffer,
    operationLog: [],
    offerCounterLimit,
    displayStatus,
    preDispute,
    region,
    type: 'primary',
    size: 'small',
  });

  const handleDetail = useCallback(() => {
    openOrderDrawer({
      entryType: 'refund',
    });
  }, [openOrderDrawer]);
  return (
    <div className="flex gap-2 justify-end flex-wrap mt-2">
      <Button
        size="small"
        className="!bg-[var(--fett-fill-300)] !border-none !text-[var(--fett-text-secondary)] hover:opacity-80"
        onClick={handleDetail}
      >
        {t('feedback.详情')}
      </Button>
      {optBtns}
    </div>
  );
};

export const ReturnItem = (props: ReturnRecord & { onUpdate: () => void }) => {
  const { platform, unit, returnSn, displayStatus, items, refundReason, onUpdate } = props;
  const { reason, reasonName, reassessedRequestReason } = refundReason;
  const isCompleted = displayStatus === 'REFUND_COMPLETED' || displayStatus === 'REFUND_SUCCESS';

  const { t } = useTranslate();
  const [opened, { toggle: toggleOpened }] = useToggle(!isCompleted);

  const { reasonLabel, reassessedRequestReasonLabel } = useReturnReasonLabel({
    platform,
    reason,
    reasonName,
    reassessedRequestReason,
  });
  const { timeTip } = useReturnTimeTip(platform, refundReason);

  return (
    <div className="flex flex-col gap-3">
      <OrderRow
        title={t('退货/退款')}
        value={<StatusText platform={platform} displayStatus={displayStatus} />}
        showArrow
        onClick={toggleOpened}
        arrowDirection={opened ? 'up' : 'down'}
      />
      {opened && (
        <div className="bg-[var(--fett-bg-subtle)] rounded p-3 flex flex-col gap-1">
          <div className="text-[var(--fett-text-primary)]">{reasonLabel}</div>
          {reassessedRequestReasonLabel && (
            <div className="text-[var(--fett-text-primary)]">{reassessedRequestReasonLabel}</div>
          )}
          <CopyText
            className="text-xs text-[var(--fett-text-tertiary)]"
            text={returnSn}
            tooltip={t('退款单号')}
            showIcon
          />
          {timeTip && (
            <div className="text-xs text-[var(--fett-surface-error-text)]">{timeTip}</div>
          )}
          <ItemsBox unit={unit} platform={platform} items={items} />
          <Operations {...props} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
};
